import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from './AuthContext';
import UserPredictionsModal from './UserPredictionsModal';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Paper,
    Button
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import Lottie from 'lottie-react';
import fireFlameAnimation from '../assets/fireFlame.json';

const RankingsTable = ({ league, rankings, selectedDate }) => {
    const { t } = useTranslation();
    const { userName } = useContext(AuthContext);
    const [showUserModal, setShowUserModal] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [selectedUserName, setSelectedUserName] = useState('');

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [hasAutoPagedToUser, setHasAutoPagedToUser] = useState(false);

    // detekce mobilu (max-width: 768px)
    const isMobile = useMediaQuery('(max-width:768px)');

    // Na mobilu fixně 10 řádků a bez volby rowsPerPage
    useEffect(() => {
        if (isMobile && rowsPerPage !== 10) {
            setRowsPerPage(10);
            setPage(0);
            setHasAutoPagedToUser(false);
        }
    }, [isMobile, rowsPerPage]);

    const handleOpenUserPredictionsModal = (userId, username) => {
        setSelectedUserId(userId);
        setSelectedUserName(username);
        setShowUserModal(true);
    };

    const handleCloseUserPredictionsModal = () => {
        setShowUserModal(false);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        if (isMobile) setHasAutoPagedToUser(true);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const safeRankings = Array.isArray(rankings) ? rankings : [];

    // Zjistíme min a max pro denní body
    const dayPointsValues = safeRankings
        .map(p => p.dayPoints)
        .filter(p => typeof p === 'number');

    const maxDayPoints = dayPointsValues.length > 0 ? Math.max(...dayPointsValues) : 0;
    const minDayPoints = dayPointsValues.length > 0 ? Math.min(...dayPointsValues) : 0;

    // Funkce pro výpočet barvy
    const getDayPointsColor = (points) => {
        if (points === undefined || points === null) return 'inherit';
        // Cílová barva: #4CAF50
        if (minDayPoints === maxDayPoints) return '#4CAF50'; 

        // Normalizujeme hodnotu 0..1
        const ratio = (points - minDayPoints) / (maxDayPoints - minDayPoints);

        // Start: #94C58C -> R=148, G=197, B=140
        // End:   #4CAF50 -> R=76,  G=175, B=80
        
        const r = Math.round(148 + (76 - 148) * ratio);
        const g = Math.round(197 + (175 - 197) * ratio);
        const b = Math.round(140 + (80 - 140) * ratio);

        return `rgb(${r}, ${g}, ${b})`;
    };

    // Seřazení žebříčku
    const sortedRankings = [...safeRankings].sort((a, b) => {
        if (b.totalPoints !== a.totalPoints) {
            return b.totalPoints - a.totalPoints;
        }
        const dayPointsA = a.dayPoints || 0;
        const dayPointsB = b.dayPoints || 0;
        if (dayPointsB !== dayPointsA) {
            return dayPointsB - dayPointsA;
        }
        return a.userId - b.userId;
    });

    let lastPoints = null;
    let lastRank = 0;
    const rankedPlayers = sortedRankings.map((player, index) => {
        if (player.totalPoints !== lastPoints) {
            lastRank = index + 1;
        }
        lastPoints = player.totalPoints;
        return { ...player, rank: lastRank };
    });

    useEffect(() => {
        if (!isMobile) return;
        if (hasAutoPagedToUser) return;
        if (rowsPerPage !== 10) return;

        const userIndex = rankedPlayers.findIndex(p => p.username === userName);
        if (userIndex >= 0) {
            const targetPage = Math.floor(userIndex / rowsPerPage);
            if (targetPage !== page) setPage(targetPage);
            setHasAutoPagedToUser(true);
        }
    }, [isMobile, rankedPlayers, rowsPerPage, userName, page, hasAutoPagedToUser]);

    const paginatedRankedPlayers = rankedPlayers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Styl pro text denních bodů
    const dayPointsStyle = {
        fontWeight: 'bold',
    };

    return (
        <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            marginTop: '20px',
            padding: '0px 10px 10px 10px'
        }}>
            <Paper
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    overflow: 'hidden',
                    backgroundColor: 'transparent',
                }}
            >
                <TableContainer sx={{ flexGrow: 1, overflowY: 'visible' }}>
                    <Table
                        stickyHeader
                        aria-label="sticky table"
                        size={isMobile ? 'small' : 'medium'}
                        sx={{
                            borderCollapse: 'separate',
                            '& .MuiTableRow-root': {
                                '&:hover': { backgroundColor: 'transparent !important' }
                            }
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        backgroundColor: '#F9F9F9',
                                        color: '#787878',
                                        fontWeight: 300,
                                        textTransform: 'uppercase',
                                        fontSize: isMobile ? '12px' : '14px',
                                        letterSpacing: '0.5px',
                                        borderTopLeftRadius: '8px',
                                        border: 'none',
                                        fontFamily: 'Montserrat, sans-serif',
                                        textAlign: 'center',
                                        width: isMobile ? '40px' : '60px',
                                        padding: isMobile ? '6px 4px' : '16px'
                                    }}
                                >
                                    {t('leagues.ranking', { defaultValue: '#' })}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        backgroundColor: '#F9F9F9',
                                        color: '#787878',
                                        fontWeight: 300,
                                        textTransform: 'uppercase',
                                        fontSize: isMobile ? '12px' : '14px',
                                        letterSpacing: '0.5px',
                                        border: 'none',
                                        fontFamily: 'Montserrat, sans-serif',
                                        padding: isMobile ? '6px 8px' : '16px'
                                    }}
                                >
                                    {t('leagueDetails.player')}
                                </TableCell>
                                {/* Sloupec "Body" (denní) - ZOBRAZIT JEN NA DESKTOPU */}
                                {!isMobile && (
                                    <TableCell
                                        align="center"
                                        sx={{
                                            backgroundColor: '#F9F9F9',
                                            color: '#787878',
                                            fontWeight: 300,
                                            textTransform: 'uppercase',
                                            fontSize: '14px',
                                            letterSpacing: '0.5px',
                                            border: 'none',
                                            fontFamily: 'Montserrat, sans-serif',
                                            width: '100px'
                                        }}
                                    >
                                        {t('leagueDetails.dayPoints', { defaultValue: 'Body' })}
                                    </TableCell>
                                )}
                                <TableCell
                                    align="right"
                                    sx={{
                                        backgroundColor: '#F9F9F9',
                                        color: '#787878',
                                        fontWeight: 300,
                                        textTransform: 'uppercase',
                                        fontSize: isMobile ? '12px' : '14px',
                                        letterSpacing: '0.5px',
                                        borderTopRightRadius: '8px',
                                        border: 'none',
                                        fontFamily: 'Montserrat, sans-serif',
                                        width: isMobile ? '50px' : '100px',
                                        padding: isMobile ? '6px 8px' : '16px'
                                    }}
                                >
                                    {t('leagueDetails.points', { defaultValue: 'Celkem' })}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedRankedPlayers.map((player) => {
                                const { rank } = player;

                                const isTopThree = rank <= 3;
                                const rowColor = rank === 1 ? 'rgba(255, 215, 0, 0.075)'
                                    : rank === 2 ? 'rgba(192, 192, 192, 0.075)'
                                    : rank === 3 ? 'rgba(205, 127, 50, 0.075)'
                                    : '#FFFFFF';

                                const hoverColor = rank === 1 ? 'rgba(255, 215, 0, 0.2)'
                                    : rank === 2 ? 'rgba(192, 192, 192, 0.2)'
                                    : rank === 3 ? 'rgba(205, 127, 50, 0.2)'
                                    : '#FAFAFA';

                                const textColor = rank === 1 ? '#998100'
                                    : rank === 2 ? '#9A9A9A'
                                    : rank === 3 ? '#CD7F32'
                                    : 'inherit';

                                const isCurrentUser = player.username === userName;
                                const globalIndex = rankedPlayers.findIndex(p => p.userId === player.userId);
                                const isSamePointsAsPrevious = globalIndex > 0 &&
                                    rankedPlayers[globalIndex].totalPoints === rankedPlayers[globalIndex - 1].totalPoints;

                                const isDayWinner = player.dayPoints === maxDayPoints && maxDayPoints > 0;

                                return (
                                    <TableRow
                                        key={player.userId}
                                        sx={{
                                            backgroundColor: rowColor + ' !important',
                                            '&:last-child td, &:last-child th': { border: 0 },
                                            borderBottom: '1px solid #e0e0e0',
                                            '&:hover': {
                                                backgroundColor: hoverColor + ' !important',
                                                '& td': { backgroundColor: hoverColor + ' !important' }
                                            },
                                            cursor: 'default',
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                fontWeight: isTopThree ? 'bold' : 'normal',
                                                border: 'none',
                                                color: textColor,
                                                textAlign: 'center',
                                                fontFamily: 'Montserrat, sans-serif',
                                                backgroundColor: rowColor + ' !important',
                                                padding: isMobile ? '6px 4px' : '16px',
                                                fontSize: isMobile ? '12px' : '14px'
                                            }}
                                        >
                                            {rank === 1 ? '🏆' :
                                                rank === 2 ? '🥈' :
                                                rank === 3 ? '🥉' :
                                                isSamePointsAsPrevious ? '-' : rank}
                                        </TableCell>
                                        
                                        {/* Buňka se jménem hráče */}
                                        <TableCell
                                            sx={{
                                                fontWeight: (isCurrentUser || isTopThree) ? 'bold' : 'normal',
                                                border: 'none',
                                                fontFamily: 'Montserrat, sans-serif',
                                                color: textColor,
                                                backgroundColor: rowColor + ' !important',
                                                padding: isMobile ? '6px 8px' : '16px',
                                                width: '100%' // Aby flex container zabral místo
                                            }}
                                        >
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'space-between', // Roztažení do stran
                                                width: '100%'
                                            }}>
                                                {/* Levá část: Jméno + Plamínek */}
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <Button
                                                        onClick={() => handleOpenUserPredictionsModal(player.userId, player.username)}
                                                        sx={{
                                                            color: isCurrentUser ? 'primary.main' : textColor,
                                                            fontWeight: (isCurrentUser || isTopThree) ? 'bold' : 'normal',
                                                            textTransform: 'none',
                                                            padding: 0,
                                                            minWidth: 'auto',
                                                            textAlign: 'left',
                                                            fontSize: isMobile ? '12px' : '14px',
                                                            lineHeight: 1.2
                                                        }}
                                                    >
                                                        {player.username}
                                                    </Button>
                                                    {isDayWinner && (
                                                        <span
                                                            title="Nejvíce bodů tento den"
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                marginLeft: '4px',
                                                                width: isMobile ? '20px' : '24px',
                                                                height: isMobile ? '20px' : '24px',
                                                                flexShrink: 0, // Aby se nezmenšil
                                                                filter: 'drop-shadow(0 0 2px rgba(255, 215, 0, 0.6))'
                                                            }}
                                                        >
                                                            <Lottie
                                                                animationData={fireFlameAnimation}
                                                                loop
                                                                autoplay
                                                                style={{ width: '100%', height: '100%' }}
                                                            />
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Pravá část: Denní body (JEN NA MOBILU) */}
                                                {isMobile && player.dayPoints !== undefined && player.dayPoints !== null && (
                                                    <span style={{ 
                                                        ...dayPointsStyle,
                                                        color: getDayPointsColor(player.dayPoints),
                                                        fontSize: '12px',
                                                        marginLeft: '8px',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        ({player.dayPoints})
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>
                                        
                                        {/* Samostatný sloupec pro denní body (JEN NA DESKTOPU) */}
                                        {!isMobile && (
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    ...dayPointsStyle,
                                                    color: getDayPointsColor(player.dayPoints),
                                                    border: 'none',
                                                    fontFamily: 'Montserrat, sans-serif',
                                                    fontSize: '15px', 
                                                    backgroundColor: rowColor + ' !important', 
                                                }}
                                            >
                                                {player.dayPoints ?? '-'}
                                            </TableCell>
                                        )}
                                        
                                        <TableCell
                                            align="right"
                                            sx={{
                                                fontWeight: isTopThree ? 'bold' : 'normal',
                                                border: 'none',
                                                color: textColor,
                                                textAlign: 'right',
                                                fontFamily: 'Montserrat, sans-serif',
                                                backgroundColor: rowColor + ' !important',
                                                padding: isMobile ? '6px 8px' : '16px',
                                                fontSize: isMobile ? '12px' : '14px'
                                            }}
                                        >
                                            {player.totalPoints}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={safeRankings.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPageOptions={isMobile ? [] : [10, 25, 100]}
                    onRowsPerPageChange={isMobile ? undefined : handleChangeRowsPerPage}
                    labelRowsPerPage={isMobile ? '' : t('rowsPerPage')}
                    labelDisplayedRows={({ from, to, count }) =>
                                `${from}—${to} ${t('of')} ${count !== -1 ? count : `${t('common.moreThan')} ${to}`}`
                            }
                    showFirstButton={!isMobile}
                    showLastButton={!isMobile}
                    sx={{
                        '.MuiTablePagination-toolbar': { p: isMobile ? 0 : 1 },
                        '.MuiTablePagination-selectLabel': { display: isMobile ? 'none' : 'inline' },
                        '.MuiTablePagination-select': { display: isMobile ? 'none' : 'inline-flex' },
                        '.MuiTablePagination-displayedRows': { fontSize: isMobile ? '12px' : '14px' },
                    }}
                />
            </Paper>
            <UserPredictionsModal
                open={showUserModal}
                league={league}
                userId={selectedUserId}
                username={selectedUserName}
                onClose={handleCloseUserPredictionsModal}
                initialSelectedDate={selectedDate}
            />
        </div>
    );
};

export default RankingsTable;